// Forge-text neutralisation, and meeting quotes as forge text. The
// `## From meeting` block itself is `meeting-block.ts`.
//
// WHY THIS EXISTS. Text Loom writes from a meeting (quotes, open questions, a
// link holder's edit) was spoken or typed by people who never meant to drive
// the forge. Written raw, `@anna` pings a user, `#42` and `group/p#3` post a
// "mentioned in" note on another issue, and a line that starts with `/` can be
// read as a GitLab quick action. So every such text goes through
// `neutraliseForgeText` before it reaches a body or a comment:
//
//  - a leading `/` (after optional spaces) is escaped as `\/`;
//  - `@user`, `#N`, `!N`, `&N` and `path#N` / `path!N` / `path&N` are wrapped
//    in a padded double-backtick code span, `` `` @user `` ``, which renders as
//    the plain token in code and is never linked. Where a backtick run would
//    merge with a neighbouring backtick (a token written against one, `#42#43`)
//    or close an earlier unmatched double run, the wrapper is a padded
//    `<code> @user </code>` instead. Backslashes right before a token go inside
//    the wrapper (`\@anna` still renders a live `@anna`).
//
// A token counts wherever the forge would link it: anywhere except right
// after a word character, so `Frage:@anna` and `Ende.@anna` are wrapped and
// `anna@panter.ch` is not. URLs, `<code>` elements, inline code spans and code
// fences are copied verbatim: the forge links nothing inside them.
//
// The padded wrappers are deliberate: they are not what a person types, so
// `stripForgeNeutraliser` can undo exactly what this module did and leave a
// person's own `` `@user` `` or `<code>@user</code>` alone. Refine
// strips a quote the model copied out of a body before matching it.
//
// A SINGLE-PASS TOKENIZER, NO LOOKBEHIND. This module ships to the browser
// (Safari 14 throws on lookbehind at parse time, where vitest cannot see it).
// "Is the previous character a word character" is a look at the OUTPUT's last
// character, and code-span state is tracked by the scan.
//
// Known limits: a code span that runs across lines is
// not seen as one; an original line that already starts with `\/`, or that
// already holds a padded wrapper around a token, does not round-trip; full
// issue URLs and GitHub's `GH-N` are not neutralised.

// Nothing here imports from `../meetings`: the bridge image copies this
// directory for `@loom/shared/i18n` and nothing of `meetings/`
// (`bridge_image_bundles_its_workspace_imports.test.ts`). The forge-text
// formatting the status comment shares lives HERE for that reason, and
// `meetings/status-comment.ts` imports it.
import { fencedLineIndices } from "./tokens.js";

/** The language of forge text Loom writes from a meeting. */
export type MeetingTextLang = "de" | "en";

const pad2 = (n: number): string => String(n).padStart(2, "0");

/** "mm:ss" from milliseconds into the meeting; minutes are not capped at 59. */
export const meetingClock = (approxMs: number): string => {
  const total = Math.max(0, Math.floor(approxMs / 1000));
  return `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`;
};

/** The meeting's day as forge text: `09.10.2026` (de) or `2026-10-09` (en). */
export const meetingDate = (dateIso: string, lang: MeetingTextLang): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/u.exec(dateIso);
  if (m === null) return dateIso;
  const [, y, mo, d] = m;
  return lang === "de" ? `${d}.${mo}.${y}` : `${y}-${mo}-${d}`;
};

/** One verified quote as forge text: `> "…" — um mm:ss herum` (de) or
 *  `> "…" — around mm:ss` (en). The text must already be on one line. The
 *  status comment and the `## From meeting` block both write it. */
export const meetingQuoteLine = (
  text: string,
  approxMs: number,
  lang: MeetingTextLang,
): string =>
  lang === "de"
    ? `> "${text}" — um ${meetingClock(approxMs)} herum`
    : `> "${text}" — around ${meetingClock(approxMs)}`;

/** GitLab and GitHub refuse a mention or a reference only right after a WORD
 *  character (`anna@panter.ch` is an address); after any other character,
 *  punctuation included (`Frage:@anna`, `Ende.@anna`), it links. */
const WORD = /[A-Za-z0-9_]/u;
const isWord = (c: string): boolean => c !== "" && WORD.test(c);

/** `@` + a user or group path. GitLab group mentions can hold `/`. */
const MENTION_AT = /@[A-Za-z0-9_][A-Za-z0-9_.\-/]*/uy;
/** `#N` (issue), `!N` (merge request), `&N` (epic), optionally behind a
 *  project path (`group/p#3`, `p!7`, `group&2`). */
const REFERENCE_AT = /(?:[A-Za-z0-9_][A-Za-z0-9_.\-/]*)?[#!&]\d+/uy;
/** A URL, copied verbatim: the forge links nothing inside a link, and a
 *  wrapped `/a#42` fragment would break it. Stops where markdown or prose
 *  could resume (`)`, quotes, brackets). */
const URL_AT = /(?:https?:\/\/|www\.)[^\s<>()[\]"'`]*/iuy;
/** `&#42;` is a character, not an epic or issue reference. */
const NUMERIC_ENTITY_AT = /&#(?:\d+|[xX][0-9A-Fa-f]+);/uy;
/** An HTML code element; the forge links nothing inside it. */
const HTML_CODE_AT = /<code>[\s\S]*?<\/code>/uy;

const TOKEN_BODY =
  "(?:@[A-Za-z0-9_][A-Za-z0-9_.\\-/]*|(?:[A-Za-z0-9_][A-Za-z0-9_.\\-/]*)?[#!&]\\d+)";
/** A whole token as `stripForgeNeutraliser` accepts it, with the backslashes
 *  that stood right before it. */
const TOKEN_EXACT = new RegExp(`^\\\\*${TOKEN_BODY}$`, "u");
/** The two wrappers, as `stripForgeNeutraliser` recognises them. */
const WRAPPED_AT = /`` ([^`\s]+) ``/uy;
const HTML_WRAPPED_AT = /<code> ([^<`\s]+) <\/code>/uy;
const ENDS_IN_HTML_WRAPPER = /<code> [^<`\s]+ <\/code>$/u;
const LEADING_SLASH = /^[ \t]*\//u;
const LEADING_ESCAPED_SLASH = /^([ \t]*)\\\//u;

/** Whether `\` + `c` is a CommonMark escape the scan copies as one unit.
 *  Not before a token's sigil: `\@anna` still renders a live `@anna`, so that
 *  backslash goes inside the wrapper instead. */
const isEscapePair = (c: string): boolean =>
  /[!-/:-@[-`{-~]/u.test(c) && !"@#!&".includes(c);

/** The padded double-backtick wrapper, or the padded `<code>` one where a
 *  backtick run would merge with a neighbouring backtick or close an earlier
 *  unmatched double run (CommonMark matches runs by exact length). */
const wrap = (token: string, html: boolean): string =>
  html ? `<code> ${token} </code>` : `\`\` ${token} \`\``;

const stickyMatch = (re: RegExp, line: string, i: number): RegExpExecArray | null => {
  re.lastIndex = i;
  return re.exec(line);
};

/** The length of the backtick run starting at `i`. */
const runAt = (line: string, i: number): number => {
  let j = i;
  while (line[j] === "`") j += 1;
  return j - i;
};

/** Where the inline code span opened by the `n`-backtick run at `i` closes
 *  (the index after its closing run), or -1 when the run is literal. */
const codeSpanEnd = (line: string, i: number, n: number): number => {
  let j = i + n;
  while (j < line.length) {
    if (line[j] !== "`") {
      j += 1;
      continue;
    }
    const m = runAt(line, j);
    if (m === n) return j + m;
    j += m;
  }
  return -1;
};

/** The token that starts at `i`, or null. A mention never ends in `.`, `-` or
 *  `/`: that is the sentence around it. */
const tokenAt = (line: string, i: number): string | null => {
  const c = line[i] ?? "";
  if (c === "@") {
    const m = stickyMatch(MENTION_AT, line, i);
    if (m === null) return null;
    let token = m[0];
    while (/[.\-/]$/u.test(token)) token = token.slice(0, -1);
    return token.length > 1 ? token : null;
  }
  if (c === "#" || c === "!" || c === "&" || isWord(c)) {
    const m = stickyMatch(REFERENCE_AT, line, i);
    return m === null ? null : m[0];
  }
  return null;
};

const neutraliseLine = (line: string): string => {
  let out = "";
  let i = 0;
  // An unmatched double-backtick run earlier on the line would pair with a
  // double-backtick wrapper and leave the token outside any code span.
  let openDouble = false;
  if (LEADING_SLASH.test(line)) {
    const slash = line.indexOf("/");
    out = `${line.slice(0, slash)}\\/`;
    i = slash + 1;
  }
  while (i < line.length) {
    const c = line[i] ?? "";
    if (c === "\\" && isEscapePair(line[i + 1] ?? "")) {
      // A CommonMark escape (`\``, `\\`, `\<`) is a character, not a run,
      // a backslash or a tag.
      out += line.slice(i, i + 2);
      i += 2;
      continue;
    }
    if (c === "`") {
      const n = runAt(line, i);
      const end = codeSpanEnd(line, i, n);
      if (end < 0 && n === 2) openDouble = true;
      const stop = end < 0 ? i + n : end;
      out += line.slice(i, stop);
      i = stop;
      continue;
    }
    if (c === "<") {
      const code = stickyMatch(HTML_CODE_AT, line, i);
      if (code !== null) {
        out += code[0];
        i += code[0].length;
        continue;
      }
    }
    // The character the forge will see before this one is the OUTPUT's last.
    if (!isWord(out[out.length - 1] ?? "")) {
      const verbatim =
        stickyMatch(URL_AT, line, i) ?? stickyMatch(NUMERIC_ENTITY_AT, line, i);
      if (verbatim !== null) {
        out += verbatim[0];
        i += verbatim[0].length;
        continue;
      }
      const token = tokenAt(line, i);
      if (token !== null) {
        // Backslashes right before the token would escape a wrapper's first
        // backtick (and `\@x` still renders a live `@x`): they go inside.
        let cut = out.length;
        while (cut > 0 && out[cut - 1] === "\\") cut -= 1;
        const lead = out.slice(cut);
        out = out.slice(0, cut);
        const before = out[out.length - 1] ?? "";
        const after = line[i + token.length] ?? "";
        out += wrap(lead + token, openDouble || before === "`" || after === "`");
        i += token.length;
        continue;
      }
    }
    out += c;
    i += 1;
  }
  return out;
};

/**
 * Text that can no longer ping a user, reference an issue, merge request or
 * epic, or run a quick action. Code fences, inline code spans, `<code>`
 * elements and URLs are left as they are: the forge links nothing inside them.
 * Idempotent.
 */
export const neutraliseForgeText = (text: string): string => {
  const lines = text.split("\n");
  const fenced = fencedLineIndices(lines);
  return lines
    .map((line, n) => (fenced.has(n) ? line : neutraliseLine(line)))
    .join("\n");
};

const stripLine = (line: string): string => {
  let out = "";
  let i = 0;
  const lead = LEADING_ESCAPED_SLASH.exec(line);
  if (lead !== null) {
    out = `${lead[1] ?? ""}/`;
    i = lead[0].length;
  }
  while (i < line.length) {
    const c = line[i] ?? "";
    if (c === "\\" && isEscapePair(line[i + 1] ?? "")) {
      out += line.slice(i, i + 2);
      i += 2;
      continue;
    }
    // A URL the neutraliser copied verbatim (it may end in a backslash).
    const url = isWord(line[i - 1] ?? "") ? null : stickyMatch(URL_AT, line, i);
    if (url !== null) {
      out += url[0];
      i += url[0].length;
      continue;
    }
    if (c === "`") {
      const w = stickyMatch(WRAPPED_AT, line, i);
      if (
        w !== null &&
        line[i + w[0].length] !== "`" &&
        TOKEN_EXACT.test(w[1] ?? "")
      ) {
        out += w[1] ?? "";
        i += w[0].length;
        continue;
      }
      // A person's own code span is copied as it is.
      const n = runAt(line, i);
      const end = codeSpanEnd(line, i, n);
      const stop = end < 0 ? i + n : end;
      out += line.slice(i, stop);
      i = stop;
      continue;
    }
    if (c === "<") {
      const w = stickyMatch(HTML_WRAPPED_AT, line, i);
      if (w !== null && TOKEN_EXACT.test(w[1] ?? "")) {
        out += w[1] ?? "";
        i += w[0].length;
        continue;
      }
      // A person's own `<code>` element is copied as it is. One that ends in
      // a wrapper's `</code>` was an unclosed `<code>` the neutraliser read as
      // text: its `<` is a character.
      const code = stickyMatch(HTML_CODE_AT, line, i);
      if (code !== null && !ENDS_IN_HTML_WRAPPER.test(code[0])) {
        out += code[0];
        i += code[0].length;
        continue;
      }
    }
    out += c;
    i += 1;
  }
  return out;
};

/** {@link neutraliseForgeText} undone: the original text, for matching a
 *  quote copied out of a body against the bytes that were said. */
export const stripForgeNeutraliser = (text: string): string => {
  const lines = text.split("\n");
  const fenced = fencedLineIndices(lines);
  return lines.map((line, n) => (fenced.has(n) ? line : stripLine(line))).join("\n");
};

// ---------------------------------------------------------------------------
// Meeting quotes as forge text: the `## From meeting` block
// (`meeting-block.ts`) and the status comment both show them.
// ---------------------------------------------------------------------------

/** What a quote helper reads of a verified quote: its text and its time. */
export interface MeetingQuoteLike {
  readonly text: string;
  readonly approxMs: number;
}

/** An issue number as forge text that names it without linking it: the
 *  wrapper `neutraliseForgeText` puts around a `#N` someone said, used where
 *  Loom names an issue itself ("Bezug: #42", "Möglicherweise schon #9"). The
 *  meeting block's link lines are live instead (`meeting-block.ts`). */
export const inertIssueRef = (iid: number): string => wrap(`#${String(iid)}`, false);

/** A quote or other caller text as ONE line with no HTML comment: text that
 *  held `<!--` or `-->` could otherwise open a comment that hides the rest of
 *  the block, or plant a hidden marker a lookup finds (`loom-meeting-ticket`,
 *  `loom-meeting-status`). The status comment cleans its quotes with it too. */
export const meetingQuoteText = (s: string): string =>
  s
    .replace(/\s+/gu, " ")
    .trim()
    .replace(/<!--/gu, "&lt;!--")
    .replace(/-->/gu, "--&gt;");

/**
 * Each sentence once. The verify step keeps one quote per role, so a
 * sentence the extract cited for two roles (the topic quote that is also the
 * commitment) is stored twice; the roles stay on the row because the draft's
 * evidence and the epic's deliverables read them. Every reader-facing render
 * (this block, the status comment, the meetings page) shows it once: same
 * cleaned text at the same time is the same utterance, and the first wins.
 */
export const uniqueMeetingQuotes = <Q extends MeetingQuoteLike>(quotes: readonly Q[]): Q[] => {
  const seen = new Set<string>();
  return quotes.filter((q) => {
    const key = `${String(Math.round(q.approxMs))}|${meetingQuoteText(q.text)}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

/**
 * Words that carry nothing on their own: backchannel, assent, hesitation,
 * a bare "nein". A quote made of nothing else ("Ja, ja, genau, genau.",
 * "Ah, nein, nein.") says nothing to a reader of the ticket.
 */
const FILLER_WORDS: ReadonlySet<string> = new Set([
  "ja", "jaja", "jo", "joa", "jep", "genau", "gut", "okay", "ok", "oke", "okey",
  "super", "perfekt", "cool", "klar", "stimmt", "richtig", "eben", "also", "doch",
  "danke", "merci", "mhm", "mm", "hm", "hmm", "aha", "ah", "äh", "ähm", "eh", "oh",
  "nein", "nee", "ne", "nö", "yes", "yeah", "yep", "no", "nope", "right",
  "exactly", "sure", "fine", "great", "thanks", "uh", "um", "uhm",
]);

/** A quote's words, lower-cased, for the comparisons below. */
export const quoteWordsOf = (text: string): string[] =>
  text
    .toLowerCase()
    .split(/[^\p{L}\p{N}]+/u)
    .filter((w) => w.length > 0);

/** Nothing but filler words (or no word at all). */
export const isFillerQuote = (text: string): boolean =>
  quoteWordsOf(text).every((w) => FILLER_WORDS.has(w));

/** A quote's words as one string, for a containment check. */
const quoteKeyOf = (text: string): string => ` ${quoteWordsOf(text).join(" ")} `;

/** Share of `a`'s distinct words also in `b`. */
const wordCoverage = (a: string, b: string): number => {
  const aw = new Set(quoteWordsOf(a));
  if (aw.size === 0) return 1;
  const bw = new Set(quoteWordsOf(b));
  let hit = 0;
  for (const w of aw) if (bw.has(w)) hit += 1;
  return hit / aw.size;
};

/** At least this share of a quote's words in a longer quote is a near-duplicate. */
export const NEAR_DUPLICATE_COVERAGE = 0.8;
/** A quote shorter than this (distinct words) is never a near-duplicate:
 *  a few common words prove nothing. */
const NEAR_DUPLICATE_MIN_WORDS = 4;

/**
 * Words that turn a sentence around. Two quotes whose words differ in one of
 * these say opposite things ("muss schneller werden" / "muss nicht schneller
 * werden", "mit Kopfzeile" / "ohne Kopfzeile"), however many words they share.
 */
const NEGATION_WORDS: ReadonlySet<string> = new Set([
  "nicht", "nichts", "kein", "keine", "keinen", "keinem", "keiner", "keines",
  "ohne", "nie", "niemals", "nirgends", "weder",
  "not", "no", "none", "nothing", "without", "never", "neither", "nor",
]);

/** Do the words that only one of the two quotes has include a negation? */
const differsInNegation = (a: string, b: string): boolean => {
  const aw = new Set(quoteWordsOf(a));
  const bw = new Set(quoteWordsOf(b));
  for (const w of aw) if (!bw.has(w) && NEGATION_WORDS.has(w)) return true;
  for (const w of bw) if (!aw.has(w) && NEGATION_WORDS.has(w)) return true;
  return false;
};

/**
 * Is `short`'s word run a run inside `long`'s: a prefix, a cut-off
 * "… dass ..." or a sub-span? `short` must not have more words than `long`.
 * This is the only repeat a listed line ("Im Meeting genannt", "Nicht im
 * Umfang") counts: one changed word in a line can be another deliverable.
 */
export const containsQuote = (short: string, long: string): boolean => {
  const s = quoteKeyOf(short);
  if (s.trim().length === 0) return true;
  if (quoteWordsOf(short).length > quoteWordsOf(long).length) return false;
  return quoteKeyOf(long).includes(s);
};

/**
 * Does `short` repeat `long`, said at the same second: {@link containsQuote},
 * or, from {@link NEAR_DUPLICATE_MIN_WORDS} distinct words on, at least
 * {@link NEAR_DUPLICATE_COVERAGE} of them are `long`'s and the words that
 * differ hold no negation? `short` must not have more words than `long`.
 */
export const repeatsQuote = (short: string, long: string): boolean => {
  if (containsQuote(short, long)) return true;
  if (quoteWordsOf(short).length > quoteWordsOf(long).length) return false;
  return (
    new Set(quoteWordsOf(short)).size >= NEAR_DUPLICATE_MIN_WORDS &&
    wordCoverage(short, long) >= NEAR_DUPLICATE_COVERAGE &&
    !differsInNegation(short, long)
  );
};
